'use client'
import { cn } from '@/lib/utils'
import type { SectionConfig } from '@/types/site'
import type { SliderContent, SlideItem } from '@/types/sections'
import { elementProps } from '@/lib/elementHelpers'
import { imageFocal } from '@/lib/contenuEditable'
import GrandTitle from '@/components/GrandTitle'
import { componentTriggerBus } from '@/lib/animations/componentTriggerBridge'
import { useSectionCarousel } from '@/hooks/useSectionCarousel'
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react'
import { EditablePlaceholder } from '../_EditablePlaceholder'
import { BrixsaViewCursor } from '../_BrixsaViewCursor'
import { useEffect, useCallback, useRef, useState } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import type { SectionMeta } from '@/components/sections'
import { palette } from '@/lib/palette'

// ═══════════════════════════════════════════════════
// Universe configs
// ═══════════════════════════════════════════════════

const UNIVERSE_CONFIGS = {
  startup: {
    bg: 'bg-white', text: 'text-zinc-900', sub: 'text-zinc-500', eyebrow: 'text-indigo-600',
    dotActive: 'bg-indigo-600', dotInactive: 'bg-zinc-300', arrowBg: 'bg-white shadow-lg hover:bg-zinc-50',
    arrowText: 'text-zinc-700', badge: 'bg-indigo-100 text-indigo-700', slideBg: 'bg-zinc-100',
    ctaBg: 'bg-indigo-600 hover:bg-indigo-700 text-white', overlay: 'from-black/60 to-transparent',
  },
  corporate: {
    bg: 'bg-slate-900', text: 'text-white', sub: 'text-slate-400', eyebrow: 'text-blue-400',
    dotActive: 'bg-blue-500', dotInactive: 'bg-slate-600', arrowBg: 'bg-slate-800 border border-slate-700 hover:bg-slate-700',
    arrowText: 'text-white', badge: 'bg-blue-900/50 text-blue-300', slideBg: 'bg-slate-800',
    ctaBg: 'bg-blue-600 hover:bg-blue-700 text-white', overlay: 'from-slate-900/80 to-transparent',
  },
  luxe: {
    bg: 'bg-white', text: 'text-zinc-800', sub: 'text-zinc-500', eyebrow: 'text-amber-700',
    dotActive: 'bg-amber-600', dotInactive: 'bg-zinc-300', arrowBg: 'bg-white border border-zinc-200 hover:bg-zinc-50',
    arrowText: 'text-zinc-700', badge: 'bg-amber-50 text-amber-800 border border-amber-200', slideBg: 'bg-zinc-50',
    ctaBg: 'bg-amber-700 hover:bg-amber-800 text-white', overlay: 'from-black/50 to-transparent',
  },
  creative: {
    bg: 'bg-amber-50', text: 'text-zinc-900', sub: 'text-zinc-600', eyebrow: 'text-zinc-900',
    dotActive: 'bg-zinc-900', dotInactive: 'bg-zinc-400', arrowBg: 'bg-yellow-300 border-2 border-zinc-900 hover:bg-yellow-400',
    arrowText: 'text-zinc-900', badge: 'bg-yellow-300 text-zinc-900 border-2 border-zinc-900', slideBg: 'bg-white border-2 border-zinc-900',
    ctaBg: 'bg-zinc-900 hover:bg-zinc-800 text-white', overlay: 'from-black/70 to-transparent',
  },
  ecommerce: {
    bg: 'bg-white', text: 'text-zinc-900', sub: 'text-zinc-500', eyebrow: 'text-emerald-600',
    dotActive: 'bg-emerald-600', dotInactive: 'bg-zinc-300', arrowBg: 'bg-white shadow-md hover:bg-zinc-50',
    arrowText: 'text-zinc-700', badge: 'bg-emerald-100 text-emerald-700', slideBg: 'bg-zinc-50',
    ctaBg: 'bg-emerald-600 hover:bg-emerald-700 text-white', overlay: 'from-black/60 to-transparent',
  },
  glass: {
    bg: 'bg-zinc-950', text: 'text-white', sub: 'text-zinc-400', eyebrow: 'text-purple-400',
    dotActive: 'bg-purple-500', dotInactive: 'bg-zinc-700', arrowBg: 'bg-white/10 backdrop-blur-sm border border-white/10 hover:bg-white/20',
    arrowText: 'text-white', badge: 'bg-purple-900/50 text-purple-300 border border-purple-700/50', slideBg: 'bg-white/5 backdrop-blur-sm border border-white/10',
    ctaBg: 'bg-purple-600 hover:bg-purple-700 text-white', overlay: 'from-zinc-950/80 to-transparent',
  },
} as const

type Universe = keyof typeof UNIVERSE_CONFIGS

function parseVariant(variant: string): { universe: Universe; layout: 'hero' | 'cards' | 'thumbnails' | 'mosaic' | 'portes' } {
  const parts = variant.split('-')
  const layoutStr = parts[parts.length - 1]
  const layout = (['hero', 'cards', 'thumbnails', 'mosaic', 'portes'].includes(layoutStr) ? layoutStr : 'hero') as 'hero' | 'cards' | 'thumbnails' | 'mosaic' | 'portes'
  const universe = (parts.slice(0, -1).join('-') || 'startup') as Universe
  return { universe: universe in UNIVERSE_CONFIGS ? universe : 'startup', layout }
}

/**
 * Layout « mosaic » — galerie éditoriale en grille asymétrique (sombre).
 * Même modèle de données que le slider (slides[]), donc éditable au CRM ;
 * on n'affiche que les images (pas de carrousel). 1er visuel large, le reste
 * en pavés. L'accent de marque (champagne) sert au sur-titre et au filet.
 */
function MosaicGallery({ config, slides }: { config: SectionConfig; slides: SlideItem[] }) {
  const content = (config.content ?? {}) as Record<string, unknown>
  const title = (content.title as string) || 'Galerie'
  const eyebrow = (content.eyebrow as string) || 'Éditions précédentes'
  const withImage = slides.filter((s) => imageFocal(s.image).src)
  return (
    <section className="bg-brand-cream py-20 md:py-28" style={{ fontFamily: 'var(--font-body, inherit)' }}>
      <div className="mx-auto max-w-6xl px-6 text-center mb-10 md:mb-14">
        <p className="uppercase" style={{ color: 'var(--color-brand-ink)', fontWeight: 600, fontSize: 'clamp(11px,0.68rem + 0.15vw,13px)', letterSpacing: '0.3em' }}>{eyebrow}</p>
        <div className="h-px mx-auto mt-5 mb-6" style={{ width: 46, backgroundColor: '#C2A66B' }} aria-hidden />
        <GrandTitle dark={false} editProps={elementProps(config.id, 'title', 'heading')}>{title}</GrandTitle>
      </div>
      {/* Mosaïque éditoriale 2 rangées pleine largeur (valeurs maquette) :
          grande photo à gauche (row-span-2), 2 tuiles larges (col-span-2). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" style={{ gridAutoRows: 'clamp(210px, 24vh, 320px)' }}>
        {withImage.slice(0, 5).map((s, i) => (
          <figure key={s.id ?? i} className={cn('relative overflow-hidden group', i === 0 && 'col-span-2 md:col-span-1 md:row-span-2', (i === 2 || i === 4) && 'md:col-span-2')}>
            <img
              {...elementProps(config.id, `slides.${i}.image`, 'image')}
              src={imageFocal(s.image).src}
              alt={s.title ?? ''}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              style={{ objectPosition: imageFocal(s.image).pos }}
            />
          </figure>
        ))}
      </div>
    </section>
  )
}

/**
 * Layout « portes » — la Constellation : grille de tuiles-photos, une par
 * univers, avec le nom superposé (voile marine au repos) et un lien par tuile.
 * Au survol : le voile s'efface, le nom disparaît, la photo fait un léger zoom.
 * Même modèle de données que le slider (slides[]) → chaque photo (image) ET
 * chaque nom (title) sont éditables au CRM ; le lien = slide.linkHref.
 */
function PortesGallery({ config, slides }: { config: SectionConfig; slides: SlideItem[] }) {
  const content = (config.content ?? {}) as Record<string, unknown>
  const title = (content.title as string) || 'Cinq univers, une même lumière'
  const eyebrow = (content.eyebrow as string) || 'La Constellation'
  const withImage = slides.filter((s) => imageFocal(s.image).src)
  /* Cette variante ignorait `style.background` : son fond était l'encre, en
     dur. Elle devenait donc un POINT FIXE dans l'alternance de la page, et sa
     parité rendait une collision inévitable plus bas (mesuré le 2026-10-06 sur
     la page « soirée à venir » : « Le Lieu » tombait en crème juste avant les
     photos du lieu, crème elles aussi). Les cartes, elles, ne changent pas :
     ce sont des images avec un voile et un texte blanc, indépendants du fond
     de la section. */
  const p = palette(config.style?.background === 'light' ? 'light' : 'dark')
  return (
    <section id={config.id} className="py-20 md:py-28" style={{ fontFamily: 'var(--font-body, inherit)', backgroundColor: p.fond }}>
      <div className="mx-auto max-w-6xl px-6">
        <div className="text-center mb-12 md:mb-16">
          <p className="uppercase font-semibold" style={{ fontSize: 'clamp(11px,0.68rem + 0.15vw,13px)', letterSpacing: '0.3em', color: p.accent }}>{eyebrow}</p>
          <div className="h-px mx-auto mt-5 mb-6" style={{ width: 46, backgroundColor: p.accent }} aria-hidden />
          <GrandTitle dark={p.sombre} editProps={elementProps(config.id, 'title', 'heading')}>{title}</GrandTitle>
          {(content.subtitle as string) && (
            <p className="mx-auto" style={{ marginTop: 22, maxWidth: 680, fontSize: 'clamp(15px, 0.9rem + 0.2vw, 17px)', lineHeight: 1.75, color: p.texte(0.78) }}>{content.subtitle as string}</p>
          )}
        </div>
      </div>
      {/* DESKTOP : colonnes dynamiques pleine largeur, texte centré sur l'image.
          MOBILE : lignes pleine largeur, texte + image côte à côte, en quinconce
          (texte gauche / image droite, puis alterné une ligne sur deux). */}
      <style>{`
        #${config.id} .portes-grid { display: grid; gap: 8px; grid-template-columns: repeat(2, 1fr); }
        #${config.id} .porte { position: relative; display: block; overflow: hidden; height: clamp(340px, 34vw, 560px); }
        #${config.id} .porte-media { position: absolute; inset: 0; }
        #${config.id} .porte-media img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; transition: transform 1s ease-out; }
        #${config.id} .porte:hover .porte-media img { transform: scale(1.08); }
        #${config.id} .porte-veil { position: absolute; inset: 0; z-index: 1; background: linear-gradient(to top, rgba(8,13,24,0.86) 6%, rgba(8,13,24,0.34) 46%, rgba(8,13,24,0) 78%); transition: opacity .5s; }
        #${config.id} .porte:hover .porte-veil { opacity: 0.12; }
        #${config.id} .porte-copy { position: absolute; inset: 0; z-index: 2; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; text-align: center; padding: 0 20px 30px; transition: opacity .5s; }
        #${config.id} .porte:hover .porte-copy { opacity: 0; }
        #${config.id} .porte-copy h3 { color: #fff; text-transform: uppercase; font-family: var(--font-heading, inherit); font-weight: 400; font-size: clamp(16px,0.9rem + 0.5vw,24px); letter-spacing: 0.12em; text-shadow: 0 1px 16px rgba(10,22,45,0.5); }
        #${config.id} .porte-copy p { color: rgba(255,255,255,0.85); margin-top: 14px; font-size: clamp(12px,0.7rem + 0.2vw,13.5px); line-height: 1.55; max-width: 24ch; text-shadow: 0 1px 14px rgba(10,22,45,0.7); }
        @media (min-width: 768px) { #${config.id} .portes-grid { grid-template-columns: repeat(${withImage.length}, 1fr); } }
        @media (max-width: 767px) {
          #${config.id} .portes-grid { display: flex; flex-direction: column; gap: 10px; }
          #${config.id} .porte { height: clamp(150px, 42vw, 200px); display: flex; align-items: stretch; background: #fff; }
          #${config.id} .porte-media { position: relative; flex: 0 0 46%; }
          #${config.id} .porte-veil { display: none; }
          #${config.id} .porte-copy { position: static; flex: 1; align-items: flex-start; justify-content: center; text-align: left; padding: 0 20px; opacity: 1 !important; }
          #${config.id} .porte-copy h3 { color: var(--color-brand-ink); text-shadow: none; font-size: 17px; }
          #${config.id} .porte-copy p { color: rgba(2,23,52,0.66); text-shadow: none; margin-top: 8px; font-size: 12.5px; max-width: none; }
          #${config.id} .porte:nth-child(even) { flex-direction: row-reverse; }
          #${config.id} .porte:hover .porte-media img { transform: none; }
        }
      `}</style>
      <div className="portes-grid">
        {withImage.map((s, i) => (
          <a
            key={s.id ?? i}
            href={s.linkHref || '#'}
            {...elementProps(config.id, `slides.${i}`, 'container', 'Porte')}
            className="porte group"
          >
            <div className="porte-media">
              <img
                {...elementProps(config.id, `slides.${i}.image`, 'image')}
                src={imageFocal(s.image).src}
                alt={s.title ?? ''}
                style={{ objectPosition: imageFocal(s.image).pos }}
              />
              <span aria-hidden className="porte-veil" />
            </div>
            <div className="porte-copy">
              <h3 {...elementProps(config.id, `slides.${i}.title`, 'heading')}>{s.title}</h3>
              {s.subtitle && (
                <p {...elementProps(config.id, `slides.${i}.subtitle`, 'text')}>{s.subtitle}</p>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════════
// Slide Components
// ═══════════════════════════════════════════════════

function HeroSlide({ slide, uConfig, sectionId, index, isEditing }: { slide: SlideItem; uConfig: typeof UNIVERSE_CONFIGS[Universe]; sectionId: string; index: number; isEditing?: boolean }) {
  return (
    <div className="embla__slide flex-[0_0_100%] min-w-0 relative">
      <div className="relative aspect-[16/7] md:aspect-[16/6] overflow-hidden rounded-lg">
        {imageFocal(slide.image).src ? (
          <img {...elementProps(sectionId, `slides.${index}.image`, 'image')} src={imageFocal(slide.image).src} alt={slide.title ?? ''} className="w-full h-full object-cover" style={{ objectPosition: imageFocal(slide.image).pos }} />
        ) : isEditing ? (
          <EditablePlaceholder sectionId={sectionId} contentPath={`slides.${index}.image`} type="image" className="w-full h-full" />
        ) : (
          <div className={cn('w-full h-full flex items-center justify-center', uConfig.slideBg)}>
            <ImageIcon className="w-16 h-16 text-zinc-400" />
          </div>
        )}
        <div className={cn('absolute inset-0 bg-gradient-to-t', uConfig.overlay)} />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
          {slide.badge ? (
            <span {...elementProps(sectionId, `slides.${index}.badge`, 'badge')} className={cn('inline-block px-3 py-1 rounded-full text-xs font-medium mb-3', uConfig.badge)}>{slide.badge}</span>
          ) : isEditing ? (
            <EditablePlaceholder sectionId={sectionId} contentPath={`slides.${index}.badge`} type="badge" />
          ) : null}
          {slide.title && (
            <h3 {...elementProps(sectionId, `slides.${index}.title`, 'heading')} className="text-2xl md:text-4xl font-bold text-white mb-2">{slide.title}</h3>
          )}
          {slide.subtitle && (
            <p {...elementProps(sectionId, `slides.${index}.subtitle`, 'text')} className="text-sm md:text-base text-white/80 max-w-xl mb-4">{slide.subtitle}</p>
          )}
          {slide.ctaLabel && (
            <a {...elementProps(sectionId, `slides.${index}.ctaLabel`, 'button')} href={slide.ctaHref ?? '#'} className={cn('inline-block px-6 py-2.5 rounded-lg text-sm font-medium transition-colors', uConfig.ctaBg)}>
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function CardSlide({ slide, uConfig, sectionId, index, isEditing }: { slide: SlideItem; uConfig: typeof UNIVERSE_CONFIGS[Universe]; sectionId: string; index: number; isEditing?: boolean }) {
  return (
    <div className="embla__slide flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 px-2">
      <div className={cn('rounded-lg overflow-hidden', uConfig.slideBg)}>
        <div className="aspect-[4/3] relative">
          {imageFocal(slide.image).src ? (
            <img {...elementProps(sectionId, `slides.${index}.image`, 'image')} src={imageFocal(slide.image).src} alt={slide.title ?? ''} className="w-full h-full object-cover" style={{ objectPosition: imageFocal(slide.image).pos }} />
          ) : isEditing ? (
            <EditablePlaceholder sectionId={sectionId} contentPath={`slides.${index}.image`} type="image" className="w-full h-full" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-zinc-200">
              <ImageIcon className="w-10 h-10 text-zinc-400" />
            </div>
          )}
          {slide.badge ? (
            <span {...elementProps(sectionId, `slides.${index}.badge`, 'badge')} className={cn('absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-medium', uConfig.badge)}>{slide.badge}</span>
          ) : isEditing ? (
            <EditablePlaceholder sectionId={sectionId} contentPath={`slides.${index}.badge`} type="badge" />
          ) : null}
        </div>
        <div className="p-4">
          {slide.title && <h3 {...elementProps(sectionId, `slides.${index}.title`, 'heading')} className={cn('font-semibold text-sm mb-1', uConfig.text)}>{slide.title}</h3>}
          {slide.subtitle && <p {...elementProps(sectionId, `slides.${index}.subtitle`, 'text')} className={cn('text-xs', uConfig.sub)}>{slide.subtitle}</p>}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════

/* Nacre parallax slide — Same technique as Brixsa but nail salon themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback. */
function NacreFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#F0E0DA',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#2A1A1E',
          } : { backgroundColor: '#2A1A1E' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(42, 26, 30, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(197, 169, 110, 0.3)', color: '#F0E0DA', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: 'var(--font-sans), sans-serif', fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#F0E0DA', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(240, 224, 218, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#2A1A1E', color: '#F0E0DA' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(42, 26, 30, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(197, 169, 110, 0.3)', color: '#F0E0DA', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: 'var(--font-sans), sans-serif', fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#F0E0DA', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(240, 224, 218, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* Obscura parallax slide — Same technique as Brixsa but photographer themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function ObscuraFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#021734',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#F7EFE1',
          } : { backgroundColor: '#F7EFE1' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(212, 168, 83, 0.2)', color: '#021734', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: 'var(--font-sans), sans-serif', fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#021734', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 228, 223, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#F7EFE1', color: '#021734' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(212, 168, 83, 0.2)', color: '#021734', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: 'var(--font-sans), sans-serif', fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#021734', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 228, 223, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* Canopy parallax slide — Same technique as Brixsa but eco/forest themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function CanopyFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#E8E8E5',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1A1A1A',
          } : { backgroundColor: '#1A1A1A' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(45, 80, 22, 0.3)', color: '#fff', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "'Inter Variable', system-ui, sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 700, lineHeight: '110%', color: '#E8E8E5', letterSpacing: '-0.02em', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 232, 229, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1A1A1A', color: '#E8E8E5' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(45, 80, 22, 0.3)', color: '#fff', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "'Inter Variable', system-ui, sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 700, lineHeight: '110%', color: '#E8E8E5', letterSpacing: '-0.02em', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 232, 229, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* ═══ Brixsa Parallax with Lightbox ═══ */
function BrixsaParallaxWithLightbox({ items, sectionId, isEditing }: { items: SlideItem[]; sectionId: string; isEditing?: boolean }) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  return (
    <section {...elementProps(sectionId, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
      {items.map((slide, i) => (
        <BrixsaFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={sectionId} isEditing={isEditing} onViewClick={() => setLightboxIndex(i)} />
      ))}
      {lightboxIndex !== null && (
        <BrixsaLightbox items={items} activeIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </section>
  )
}

/* ═══ Brixsa Lightbox — Luxury fullscreen gallery modal ═══ */
function BrixsaLightbox({ items, activeIndex, onClose }: { items: SlideItem[]; activeIndex: number; onClose: () => void }) {
  const [current, setCurrent] = useState(activeIndex)
  const [fade, setFade] = useState(true)

  useEffect(() => { setCurrent(activeIndex) }, [activeIndex])

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') goNext()
      if (e.key === 'ArrowLeft') goPrev()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  })

  const goTo = (i: number) => {
    setFade(false)
    setTimeout(() => { setCurrent(i); setFade(true) }, 200)
  }
  const goNext = () => goTo((current + 1) % items.length)
  const goPrev = () => goTo((current - 1 + items.length) % items.length)

  const slide = items[current]

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      backgroundColor: 'rgba(5, 5, 5, 0.96)',
      backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)',
      display: 'flex', flexDirection: 'column',
      animation: 'brixsaLightboxIn 0.4s ease-out',
    }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes brixsaLightboxIn { from { opacity: 0; } to { opacity: 1; } }
      ` }} />

      {/* Top bar — close + counter */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(16px, 3vw, 32px) clamp(20px, 5vw, 60px)',
        flexShrink: 0,
      }}>
        <span style={{
          fontFamily: 'var(--font-body, inherit)', fontSize: '13px', fontWeight: 400,
          color: 'rgba(255,255,255,0.5)', letterSpacing: '0.2em',
        }}>
          {String(current + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}
        </span>
        <div role="button" onClick={onClose} style={{ cursor: 'pointer', padding: '8px' }}>
          <span style={{ width: '28px', height: '20px', position: 'relative', display: 'block' }}>
            <span style={{ width: '28px', height: '2px', display: 'block', position: 'absolute', backgroundColor: 'white', left: 0, top: '9px', transform: 'rotate(45deg)', transformOrigin: 'center' }} />
            <span style={{ width: '28px', height: '2px', display: 'block', position: 'absolute', backgroundColor: 'white', left: 0, top: '9px', transform: 'rotate(-45deg)', transformOrigin: 'center' }} />
          </span>
        </div>
      </div>

      {/* Image area */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
        padding: '0 clamp(60px, 8vw, 120px)',
      }}>
        {/* Left arrow */}
        <div role="button" onClick={goPrev} style={{
          position: 'absolute', left: 'clamp(12px, 2vw, 32px)', top: '50%', transform: 'translateY(-50%)',
          cursor: 'pointer', padding: '16px', zIndex: 2,
          opacity: 0.5, transition: 'opacity 0.3s',
        }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
        </div>

        {/* Image */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageFocal(slide?.image).src}
          alt={slide?.title || ''}
          style={{
            maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain',
            objectPosition: imageFocal(slide?.image).pos,
            borderRadius: '2px',
            opacity: fade ? 1 : 0,
            transition: 'opacity 0.2s ease',
            boxShadow: '0 20px 80px rgba(0,0,0,0.6)',
          }}
        />

        {/* Right arrow */}
        <div role="button" onClick={goNext} style={{
          position: 'absolute', right: 'clamp(12px, 2vw, 32px)', top: '50%', transform: 'translateY(-50%)',
          cursor: 'pointer', padding: '16px', zIndex: 2,
          opacity: 0.5, transition: 'opacity 0.3s',
        }} onMouseEnter={e => e.currentTarget.style.opacity = '1'} onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
        </div>
      </div>

      {/* Bottom — title + subtitle */}
      <div style={{
        padding: 'clamp(16px, 3vw, 32px) clamp(20px, 5vw, 60px)',
        textAlign: 'center', flexShrink: 0,
        opacity: fade ? 1 : 0, transition: 'opacity 0.2s ease',
      }}>
        <h3 style={{
          fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(1.5rem, 1rem + 2vw, 2.5rem)',
          fontWeight: 400, fontStyle: 'italic', color: 'white', marginBottom: '8px',
        }}>
          {slide?.title}
        </h3>
        <p style={{
          fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(13px, 0.8125rem + 0.2vw, 15px)', fontWeight: 400,
          color: 'rgba(255,255,255,0.5)', maxWidth: '500px', margin: '0 auto',
        }}>
          {slide?.subtitle}
        </p>

        {/* Thumbnails */}
        <div style={{
          display: 'flex', gap: '8px', justifyContent: 'center', marginTop: '24px',
        }}>
          {items.map((item, i) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={item.id}
              src={imageFocal(item.image).src}
              alt=""
              onClick={() => goTo(i)}
              style={{
                width: '48px', height: '48px', objectFit: 'cover', objectPosition: imageFocal(item.image).pos, borderRadius: '2px',
                cursor: 'pointer',
                opacity: i === current ? 1 : 0.35,
                border: i === current ? '1px solid var(--color-accent, #021734)' : '1px solid transparent',
                transition: 'opacity 0.3s, border-color 0.3s',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/* Brixsa fixed-bg slide — routing par contexte :
   - Desktop preview : CSS background-attachment:fixed natif (compositor
     browser, hard clip per section, pixel-perfect — comportement initial).
   - Mobile preview : position:fixed + IntersectionObserver avec toggle
     binary opacity 0/1 (pas de crossfade). L'image apparaît hard quand
     le slide est majoritairement visible. Compositor-driven aussi, zéro
     lag scroll mobile (où bg-attachment fixed est cassé sur Android Chrome
     et iOS Safari).
   - Editor mode : image position:absolute dans le slide (fallback car
     position:fixed escape le canvas éditeur). */
function BrixsaFixedBgSlide({ slide, idx, sectionId, isEditing, onViewClick }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean; onViewClick?: () => void }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const fixedImgRef = useRef<HTMLImageElement>(null)
  const isMobile = useIsMobile()

  // Mobile preview only : position:fixed image au viewport + clip-path
  // qui clippe l'image aux bornes visibles du slide. Mimique exacte de
  // background-attachment:fixed (image fixe dans le viewport, visible
  // uniquement dans la zone du slide). Continuous rAF gardé par IO
  // pour ne tourner que quand le slide est en view.
  useEffect(() => {
    if (isEditing || !isMobile) return
    const wrap = wrapRef.current
    const img = fixedImgRef.current
    if (!wrap || !img) return

    let inView = false
    let rafId: number | null = null

    const update = () => {
      const rect = wrap.getBoundingClientRect()
      /* Root cause fix bande Le Lieu vs Vedette : on utilise img.offsetHeight
         (= 100lvh en px, valeur FIXE indépendante de l'URL bar mobile) au lieu
         de window.innerHeight (qui vaut svh quand l'URL bar est visible, lvh
         quand cachée). Avec window.innerHeight, quand l'URL bar est visible :
         vh = svh < imgHeight = lvh, donc clip-path bottom inset = vh - bottom
         = svh - 100svh = 0 → image PAS clippée → s'étend de 100svh à 100lvh
         (10-15% sous le slide) et recouvre la section suivante. Symptôme :
         "bande" visible en bas du slide Le Lieu (URL bar pas encore cachée).
         Sur Vedette, URL bar déjà cachée donc vh=lvh=imgHeight, formule
         redonne le bon résultat — d'où l'absence du bug à cet endroit. */
      const imgHeight = img.offsetHeight
      const top = Math.max(0, rect.top)
      const bottom = Math.min(imgHeight, rect.bottom)

      if (bottom <= top) {
        img.style.opacity = '0'
      } else {
        const isExitingTop = rect.top < 0
        const visiblePct = (bottom - top) / imgHeight
        const fadeThreshold = 0.03
        const opacity = isExitingTop && visiblePct < fadeThreshold
          ? Math.max(0, visiblePct / fadeThreshold)
          : 1
        img.style.opacity = opacity.toFixed(3)
        /* Safety margin de 2px sur l'inset bottom : rect.bottom est un float
           (sub-pixel) et img.offsetHeight un entier. Sans marge, ~0.5-1px de
           l'image peut "fuir" sous le bord rendu du slide (le "trou de 1mm"
           observé après le fix lvh). Cliquer 2px plus tôt résout sans
           perte visible (le bas du slide a déjà le gradient overlay sombre). */
        img.style.clipPath = `inset(${top.toFixed(2)}px 0 ${(imgHeight - bottom + 2).toFixed(2)}px 0)`
      }
      rafId = inView ? requestAnimationFrame(update) : null
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!inView) {
            inView = true
            /* Premier tick : on calcule le clip-path en synchrone pour
               éviter un flash de 1 frame à opacity 0 (le slide a un
               background transparent sur mobile → on verrait le noir
               du body derrière). Le rAF récursif est ensuite démarré
               par update() lui-même via `rafId = inView ? rAF(update) : null`. */
            update()
          }
        } else {
          inView = false
          if (rafId !== null) {
            cancelAnimationFrame(rafId)
            rafId = null
          }
          img.style.opacity = '0'
        }
      },
      { threshold: 0 }
    )
    observer.observe(wrap)

    return () => {
      inView = false
      observer.disconnect()
      if (rafId !== null) cancelAnimationFrame(rafId)
    }
  }, [isEditing, isMobile])

  // ───── Desktop preview : CSS background-attachment: fixed natif ─────
  if (!isEditing && !isMobile) {
    /* Si slide.linkHref est défini, on wrappe le slide dans un <a> et on
       inhibe onClick → le clic est dispatché en tant que clic d'anchor,
       capté par le listener global de EventGallery (a[href^="#galerie"]).
       Sinon, comportement classique : onClick déclenche le mini-lightbox
       interne du carrousel. */
    const desktopInner = (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        onClick={slide.linkHref ? undefined : onViewClick}
        style={{ display: 'block', cursor: (slide.linkHref || onViewClick) ? 'pointer' : 'default' }}
      >
        <style dangerouslySetInnerHTML={{ __html: `
          @media (max-width: 640px) {
            .brixsa-slide-row { flex-direction: column !important; align-items: flex-start !important; gap: 14px !important; }
          }
          .brixsa-agrandir-hint {
            position: absolute;
            bottom: clamp(15px, 2.5vw, 25px);
            right: clamp(20px, 5vw, 60px);
            z-index: 3;
            font-family: var(--font-heading, inherit);
            font-style: italic;
            font-weight: 400;
            font-size: clamp(13px, 0.8125rem + 0.2vw, 15px);
            color: rgba(245, 240, 232, 0.55);
            letter-spacing: 0.02em;
            transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            pointer-events: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          .brixsa-agrandir-chevron {
            display: inline-block;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .brixsa-parallax-slide:hover .brixsa-agrandir-hint { color: rgba(245, 240, 232, 1); }
          .brixsa-parallax-slide:hover .brixsa-agrandir-chevron { transform: translateX(5px); }
        ` }} />
        <BrixsaViewCursor
          className="brixsa-parallax-slide"
          style={{
            minHeight: '100svh',
            position: 'relative',
            overflow: 'hidden',
            color: 'var(--color-background, #e1e1e1)',
            ...(imageFocal(slide.image).src ? {
              backgroundImage: `url(${imageFocal(slide.image).src})`,
              backgroundAttachment: 'fixed',
              backgroundSize: 'cover',
              backgroundPosition: imageFocal(slide.image).pos,
              backgroundColor: 'var(--color-primary, #4a2711)',
            } : { backgroundColor: 'var(--color-primary, #4a2711)' }),
          }}
        >
          <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(20, 12, 8, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
          {!!(slide as unknown as Record<string, unknown>).featured && (
            <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', background: 'rgba(158,158,158,0.3)', color: '#fff', fontSize: 'clamp(13px, 0.8125rem + 0.3vw, 16px)', fontWeight: 400, zIndex: 2, fontFamily: 'var(--font-body, inherit)', letterSpacing: '0.15em' }}>
              Featured
            </div>
          )}
          <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} className="brixsa-slide-content" style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
            <div className="brixsa-slide-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', gap: 'clamp(16px, 4vw, 32px)' }}>
              <div style={{ maxWidth: '680px' }}>
                <h2 {...elementProps(sectionId, `slides.${idx}.title`, 'heading')} style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(2.5rem, 2rem + 2.2vw, 3.5rem)', fontWeight: 400, fontStyle: 'italic', lineHeight: '105%', color: '#fff', marginBottom: 16 }}>{slide.title}</h2>
                <p {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')} style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(13px, 0.8125rem + 0.2vw, 15px)', lineHeight: '170%', color: 'rgba(255,255,255,0.6)', fontWeight: 400, maxWidth: '480px' }}>{slide.subtitle}</p>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, whiteSpace: 'nowrap' }}>
                <div style={{ width: '40px', height: '1px', backgroundColor: 'rgba(255,255,255,0.3)', marginBottom: 4 }} />
                <h5 {...elementProps(sectionId, `slides.${idx}.badge`, 'text')} style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(10px, 0.625rem + 0.15vw, 12px)', fontWeight: 400, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>{slide.badge}</h5>
              </div>
            </div>
          </div>
          {/* Hint cliquable ultra-discret — italique Cormorant, opacity 0.55 → 1 au hover, chevron qui glisse */}
          <span aria-hidden className="brixsa-agrandir-hint">
            agrandir <span className="brixsa-agrandir-chevron">›</span>
          </span>
        </BrixsaViewCursor>
      </div>
    )
    return slide.linkHref ? (
      <a href={slide.linkHref} style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}>
        {desktopInner}
      </a>
    ) : desktopInner
  }

  // ───── Mobile preview ou éditeur ─────
  /* Si slide.linkHref est défini hors éditeur, on wrap le slide dans <a> et
     on inhibe onClick (idem branche desktop ci-dessus). */
  const useLink = !!slide.linkHref && !isEditing
  return (
    <>
      {/* Mobile preview only : fixed bg image OUTSIDE slide flow.
          IO toggle binary opacity 0/1 → hard switch entre slides au scroll.
          Premier slide eager (déjà visible au load). Suivants en lazy pour
          ne pas saturer le LCP avec 7 images parallèles. */}
      {!isEditing && isMobile && imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={fixedImgRef}
          src={imageFocal(slide.image).src}
          alt=""
          aria-hidden
          loading={idx === 0 ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={idx === 0 ? 'high' : 'low'}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100lvh',
            objectFit: 'cover',
            objectPosition: imageFocal(slide.image).pos,
            opacity: 0,
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
      )}

      <div
        ref={wrapRef}
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        onClick={(!isEditing && !slide.linkHref) ? onViewClick : undefined}
        className="brixsa-parallax-slide"
        style={{
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          // Mobile preview : transparent pour laisser l'image fixed (en
          // arrière-plan via clip-path) traverser. Editor : bg brun comme
          // fallback (l'éditeur rend l'image en absolute dans le slide).
          backgroundColor: isEditing ? 'var(--color-primary, #4a2711)' : 'transparent',
          color: 'var(--color-background, #e1e1e1)',
          cursor: !isEditing && (onViewClick || slide.linkHref) ? 'pointer' : undefined,
        }}
      >
        {/* Anchor overlay : couvre tout le slide quand linkHref est défini.
            Positionné absolute dans le slide (parent : position:relative).
            z-index 10 → au-dessus du contenu (titre, badge, hint = zIndex 1-3).
            Le clic ouvre le modal EventGallery via le listener global. */}
        {useLink && (
          <a
            href={slide.linkHref}
            aria-label={slide.title ? `Voir la galerie : ${slide.title}` : 'Ouvrir la galerie'}
            style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'block' }}
          />
        )}
        {/* Editor only : image en absolute dans le slide. */}
        {isEditing && imageFocal(slide.image).src && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
            src={imageFocal(slide.image).src}
            alt=""
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none' }}
          />
        )}

        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(20, 12, 8, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {!!(slide as unknown as Record<string, unknown>).featured && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(30px)', WebkitBackdropFilter: 'blur(30px)', background: 'rgba(158,158,158,0.3)', color: '#fff', fontSize: 'clamp(13px, 0.8125rem + 0.3vw, 16px)', fontWeight: 400, zIndex: 2, fontFamily: 'var(--font-body, inherit)', letterSpacing: '0.15em' }}>
            Featured
          </div>
        )}
        <style dangerouslySetInnerHTML={{ __html: `
          @media (max-width: 640px) {
            .brixsa-slide-row { flex-direction: column !important; align-items: flex-start !important; gap: 14px !important; }
          }
          .brixsa-agrandir-hint {
            position: absolute;
            bottom: clamp(15px, 2.5vw, 25px);
            right: clamp(20px, 5vw, 60px);
            z-index: 3;
            font-family: var(--font-heading, inherit);
            font-style: italic;
            font-weight: 400;
            font-size: clamp(13px, 0.8125rem + 0.2vw, 15px);
            color: rgba(245, 240, 232, 0.55);
            letter-spacing: 0.02em;
            transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            pointer-events: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
          }
          .brixsa-agrandir-chevron {
            display: inline-block;
            transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .brixsa-parallax-slide:hover .brixsa-agrandir-hint { color: rgba(245, 240, 232, 1); }
          .brixsa-parallax-slide:hover .brixsa-agrandir-chevron { transform: translateX(5px); }
          /* Mobile : pas de hover possible, on cache le hint "agrandir" → sinon
             c'est juste un texte fantôme qui n'a aucune utilité tactile. */
          @media (max-width: 768px) {
            .brixsa-agrandir-hint { display: none !important; }
          }
        ` }} />
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} className="brixsa-slide-content" style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div className="brixsa-slide-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', gap: 'clamp(16px, 4vw, 32px)' }}>
            <div style={{ maxWidth: '680px' }}>
              <h2 {...elementProps(sectionId, `slides.${idx}.title`, 'heading')} style={{ fontFamily: 'var(--font-heading, inherit)', fontSize: 'clamp(2.5rem, 2rem + 2.2vw, 3.5rem)', fontWeight: 400, fontStyle: 'italic', lineHeight: '105%', color: '#fff', marginBottom: 16 }}>{slide.title}</h2>
              <p {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')} style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(13px, 0.8125rem + 0.2vw, 15px)', lineHeight: '170%', color: 'rgba(255,255,255,0.6)', fontWeight: 400, maxWidth: '480px' }}>{slide.subtitle}</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, whiteSpace: 'nowrap' }}>
              <div style={{ width: '40px', height: '1px', backgroundColor: 'rgba(255,255,255,0.3)', marginBottom: 4 }} />
              <h5 {...elementProps(sectionId, `slides.${idx}.badge`, 'text')} style={{ fontFamily: 'var(--font-body, inherit)', fontSize: 'clamp(10px, 0.625rem + 0.15vw, 12px)', fontWeight: 400, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>{slide.badge}</h5>
            </div>
          </div>
        </div>
        {/* Hint "agrandir" — visible en hover desktop uniquement (cf. media query
            ci-dessus qui le masque < 768px). */}
        <span aria-hidden className="brixsa-agrandir-hint">
          agrandir <span className="brixsa-agrandir-chevron">›</span>
        </span>
      </div>
    </>
  )
}

/* Braise parallax slide — Same technique as Nacre but restaurant themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function BraiseFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#021734',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1A1209',
          } : { backgroundColor: '#1A1209' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 18, 9, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(114, 47, 55, 0.3)', color: '#021734', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#F5F0E8', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 228, 223, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1A1209', color: '#021734' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 18, 9, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(114, 47, 55, 0.3)', color: '#021734', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#F5F0E8', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 228, 223, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* Forge parallax slide — Same technique as Braise but sports coach themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function ForgeFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#E8E8E8',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#F7EFE1',
          } : { backgroundColor: '#F7EFE1' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(255, 77, 0, 0.3)', color: '#E8E8E8', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E8E8E8', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 232, 232, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#F7EFE1', color: '#E8E8E8' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(255, 77, 0, 0.3)', color: '#E8E8E8', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E8E8E8', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(232, 232, 232, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* Ciseaux parallax slide — Hair salon themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function CiseauxFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#0B0B0B',
          } : { backgroundColor: '#0B0B0B' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(11, 11, 11, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(183, 110, 121, 0.3)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#FFFFFF', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255, 255, 255, 0.8)' }}
            >
              {slide.subtitle}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#0B0B0B', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(11, 11, 11, 0.8) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(183, 110, 121, 0.3)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#FFFFFF', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255, 255, 255, 0.8)' }}
          >
            {slide.subtitle}
          </p>
        </div>
      </div>
    </div>
  )
}

/* Atelier parallax slide — Interior architecture themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
/* Encre parallax slide — Same technique as Braise/Atelier but tattoo studio themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback with translate3d GPU acceleration. */
function EncreFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#F7EFE1',
          } : { backgroundColor: '#F7EFE1' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.90) 14%, rgba(10, 10, 10, 0.3) 60%, rgba(10, 10, 10, 0.1) 100%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(196, 30, 58, 0.25)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(196, 30, 58, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C41E3A', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: '#8C8C8C' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#C41E3A', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#F7EFE1', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(10, 10, 10, 0.90) 14%, rgba(10, 10, 10, 0.3) 60%, rgba(10, 10, 10, 0.1) 100%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(196, 30, 58, 0.25)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(196, 30, 58, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C41E3A', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: '#8C8C8C' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#C41E3A', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function AtelierFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1A1A1A',
          } : { backgroundColor: '#1A1A1A' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(196, 181, 160, 0.25)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(196, 181, 160, 0.3)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C4B5A0', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255, 255, 255, 0.8)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#8B7355', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1A1A1A', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.85) 14%, rgba(255, 255, 255, 0) 39%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(196, 181, 160, 0.25)', color: '#FFFFFF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(196, 181, 160, 0.3)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C4B5A0', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255, 255, 255, 0.8)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#8B7355', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function SereniteFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1B1B2F',
          } : { backgroundColor: '#1B1B2F' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(27, 27, 47, 0.90) 14%, rgba(27, 27, 47, 0.3) 60%, rgba(27, 27, 47, 0.1) 100%)' }} />
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(212, 184, 150, 0.25)', color: '#D4B896', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(212, 184, 150, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#D4B896', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: '#C8C0D4' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#D4B896', color: '#1B1B2F', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1B1B2F', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(27, 27, 47, 0.90) 14%, rgba(27, 27, 47, 0.3) 60%, rgba(27, 27, 47, 0.1) 100%)' }} />
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(212, 184, 150, 0.25)', color: '#D4B896', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.05em', border: '1px solid rgba(212, 184, 150, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#D4B896', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: '#C8C0D4' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#D4B896', color: '#1B1B2F', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function PulseFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#0D0D0D',
          } : { backgroundColor: '#0D0D0D' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(13, 13, 13, 0.92) 14%, rgba(13, 13, 13, 0.4) 60%, rgba(13, 13, 13, 0.15) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#00E5FF', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(0, 229, 255, 0.12)', color: '#00E5FF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(0, 229, 255, 0.35)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#00E5FF', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.7)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#FF006E', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#0D0D0D', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(13, 13, 13, 0.92) 14%, rgba(13, 13, 13, 0.4) 60%, rgba(13, 13, 13, 0.15) 100%)' }} />
      {/* Slide number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#00E5FF', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(0, 229, 255, 0.12)', color: '#00E5FF', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(0, 229, 255, 0.35)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#00E5FF', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.7)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#FF006E', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/* Saveur parallax slide — Same technique as Serenite but catering/private chef themed.
 * Brown black (#1C1917) bg, gold (#C8A97E) slide numbers/titles, sienna (#6B4C3B) CTA. */
function SaveurFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1C1917',
          } : { backgroundColor: '#1C1917' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(28, 25, 23, 0.92) 14%, rgba(28, 25, 23, 0.35) 60%, rgba(28, 25, 23, 0.12) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#C8A97E', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(200, 169, 126, 0.2)', color: '#C8A97E', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(200, 169, 126, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C8A97E', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#6B4C3B', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1C1917', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(28, 25, 23, 0.92) 14%, rgba(28, 25, 23, 0.35) 60%, rgba(28, 25, 23, 0.12) 100%)' }} />
      {/* Slide number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#C8A97E', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(200, 169, 126, 0.2)', color: '#C8A97E', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(200, 169, 126, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#C8A97E', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#6B4C3B', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/* Ascent parallax slide — Same technique as Saveur but business/life coach themed.
 * Dark navy (#111827) bg, gold (#E0B870) slide numbers/titles, slate (#2D3748) CTA. */
function AscentFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#111827',
          } : { backgroundColor: '#111827' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(17, 24, 39, 0.92) 14%, rgba(17, 24, 39, 0.35) 60%, rgba(17, 24, 39, 0.12) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#E0B870', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(224, 184, 112, 0.15)', color: '#E0B870', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(224, 184, 112, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E0B870', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#2D3748', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#111827', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(17, 24, 39, 0.92) 14%, rgba(17, 24, 39, 0.35) 60%, rgba(17, 24, 39, 0.12) 100%)' }} />
      {/* Slide number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#E0B870', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(224, 184, 112, 0.15)', color: '#E0B870', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(224, 184, 112, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E0B870', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#2D3748', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function ZenithFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#1A1A1A',
          } : { backgroundColor: '#1A1A1A' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.92) 14%, rgba(26, 26, 26, 0.35) 60%, rgba(26, 26, 26, 0.10) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#A8C5A0', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(168, 197, 160, 0.15)', color: '#A8C5A0', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(168, 197, 160, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#A8C5A0', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#D4C5B0', color: '#1A1A1A', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#1A1A1A', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(26, 26, 26, 0.92) 14%, rgba(26, 26, 26, 0.35) 60%, rgba(26, 26, 26, 0.10) 100%)' }} />
      {/* Slide number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#A8C5A0', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(168, 197, 160, 0.15)', color: '#A8C5A0', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(168, 197, 160, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#A8C5A0', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#D4C5B0', color: '#1A1A1A', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/* Miel parallax slide — Same technique as Zenith but patisserie / bakery themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback. */
function MielFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: '#2A1F1A',
          } : { backgroundColor: '#2A1F1A' }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(42, 31, 26, 0.92) 14%, rgba(42, 31, 26, 0.35) 60%, rgba(42, 31, 26, 0.10) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#E8C17A', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(232, 193, 122, 0.15)', color: '#E8C17A', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(232, 193, 122, 0.4)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '680px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E8C17A', textTransform: 'capitalize', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#8B5E3C', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: '#2A1F1A', color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(42, 31, 26, 0.92) 14%, rgba(42, 31, 26, 0.35) 60%, rgba(42, 31, 26, 0.10) 100%)' }} />
      {/* Slide number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: '#E8C17A', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 4, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(232, 193, 122, 0.15)', color: '#E8C17A', fontSize: 14, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(232, 193, 122, 0.4)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "var(--font-sans), sans-serif", fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)', fontWeight: 500, lineHeight: '110%', color: '#E8C17A', textTransform: 'capitalize', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 16, lineHeight: '150%', color: 'rgba(255,255,255,0.72)' }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: '#8B5E3C', color: '#FFFFFF', borderRadius: '4px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

/* JL Studio process slide — Dark agency theme with blue accent.
   Same parallax technique as Miel but with #0a0a0f bg and #638BFF accent. */
function JlstudioFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  const BG = '#0a0a0f'
  const ACCENT = '#638BFF'

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: BG,
          } : { backgroundColor: BG }),
        }}
      >
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: `linear-gradient(360deg, ${BG}ee 14%, ${BG}88 55%, ${BG}22 100%)` }} />
        {/* Step number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "'Inter Variable', 'Inter', sans-serif", fontSize: '13px', fontWeight: 600, color: ACCENT, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          Étape {String(idx + 1).padStart(2, '0')}
        </div>
        {slide.badge && (
          <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 100, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(99, 139, 255, 0.12)', color: ACCENT, fontSize: 13, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(99, 139, 255, 0.3)' }}>
            {slide.badge}
          </div>
        )}
        <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
          <div style={{ maxWidth: '700px' }}>
            <h2
              {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
              style={{ fontFamily: "'Inter Variable', 'Inter', sans-serif", fontSize: 'clamp(2.5rem, 1.5rem + 4vw, 4.5rem)', fontWeight: 600, lineHeight: '110%', color: '#FFFFFF', marginBottom: 20 }}
            >
              {slide.title}
            </h2>
            <p
              {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
              style={{ fontSize: 17, lineHeight: '160%', color: 'rgba(255,255,255,0.65)', maxWidth: 560 }}
            >
              {slide.subtitle}
            </p>
            {slide.ctaLabel && (
              <a
                {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
                href={slide.ctaHref ?? '#'}
                style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: ACCENT, color: '#FFFFFF', borderRadius: '100px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
              >
                {slide.ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: BG, color: '#FFFFFF' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: `linear-gradient(360deg, ${BG}ee 14%, ${BG}88 55%, ${BG}22 100%)` }} />
      {/* Step number top-right */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "'Inter Variable', 'Inter', sans-serif", fontSize: '13px', fontWeight: 600, color: ACCENT, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        Étape {String(idx + 1).padStart(2, '0')}
      </div>
      {slide.badge && (
        <div {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')} style={{ position: 'absolute', left: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', padding: '8px 20px', borderRadius: 100, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', background: 'rgba(99, 139, 255, 0.12)', color: ACCENT, fontSize: 13, fontWeight: 500, zIndex: 2, letterSpacing: '0.08em', textTransform: 'uppercase', border: '1px solid rgba(99, 139, 255, 0.3)' }}>
          {slide.badge}
        </div>
      )}
      <div {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')} style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}>
        <div style={{ maxWidth: '700px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{ fontFamily: "'Inter Variable', 'Inter', sans-serif", fontSize: 'clamp(2.5rem, 1.5rem + 4vw, 4.5rem)', fontWeight: 600, lineHeight: '110%', color: '#FFFFFF', marginBottom: 20 }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{ fontSize: 17, lineHeight: '160%', color: 'rgba(255,255,255,0.65)', maxWidth: 560 }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{ display: 'inline-block', marginTop: '28px', padding: '12px 28px', background: ACCENT, color: '#FFFFFF', borderRadius: '100px', fontSize: '15px', fontWeight: 500, textDecoration: 'none', letterSpacing: '0.03em', transition: 'background 0.3s ease' }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

function PrismeFixedBgSlide({ slide, idx, sectionId, isEditing }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const lensRef = useRef<HTMLDivElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  // Scroll-based lens rotation, progress indicator & content reveal
  useEffect(() => {
    const wrap = wrapRef.current
    const lens = lensRef.current
    const progress = progressRef.current
    const content = contentRef.current
    if (!wrap) return

    // Find the scroll container
    let scroller: HTMLElement | Window = window
    if (isEditing) {
      let el: HTMLElement | null = wrap.parentElement
      while (el) {
        const ov = getComputedStyle(el).overflowY
        if (ov === 'auto' || ov === 'scroll') { scroller = el; break }
        el = el.parentElement
      }
    }

    const getScrollTop = () => scroller instanceof Window ? window.scrollY : scroller.scrollTop
    const getViewH = () => scroller instanceof Window ? window.innerHeight : scroller.clientHeight

    let rafId = 0
    const onScroll = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const rect = wrap.getBoundingClientRect()
        const viewH = getViewH()

        // Progress within this slide (0 at top of viewport, 1 at bottom)
        const slideProgress = Math.max(0, Math.min(1, 1 - (rect.bottom / (viewH + rect.height))))

        // Lens rotation (slow — maps 0..1 to 0..180deg)
        if (lens) {
          lens.style.transform = `translate3d(0,0,0) rotate(${slideProgress * 180}deg)`
        }

        // Progress bar at bottom of slide
        if (progress) {
          progress.style.transform = `scaleX(${slideProgress})`
        }

        // Content reveal when slide enters view
        const enteredView = rect.top < viewH * 0.7
        if (enteredView && !isInView) {
          setIsInView(true)
        }
      })
    }

    const target = scroller instanceof Window ? window : scroller
    target.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    // IntersectionObserver for in-view detection
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsInView(true)
    }, { threshold: 0.25 })
    obs.observe(wrap)

    return () => {
      target.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(rafId)
      obs.disconnect()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  const navy = '#0F1923'
  const iceBlue = '#B8D4E3'

  // Decorative lens SVG
  const lensDecoration = (
    <div
      ref={lensRef}
      style={{
        position: 'absolute',
        right: 'clamp(40px, 8vw, 120px)',
        top: '50%',
        marginTop: '-120px',
        width: '240px',
        height: '240px',
        zIndex: 1,
        pointerEvents: 'none',
        opacity: 0.07,
        willChange: 'transform',
      }}
    >
      <svg viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
        <circle cx="120" cy="120" r="110" stroke={iceBlue} strokeWidth="1" />
        <circle cx="120" cy="120" r="85" stroke={iceBlue} strokeWidth="0.5" />
        <circle cx="120" cy="120" r="55" stroke={iceBlue} strokeWidth="0.5" />
        <line x1="120" y1="10" x2="120" y2="230" stroke={iceBlue} strokeWidth="0.3" />
        <line x1="10" y1="120" x2="230" y2="120" stroke={iceBlue} strokeWidth="0.3" />
        <line x1="42" y1="42" x2="198" y2="198" stroke={iceBlue} strokeWidth="0.3" />
        <line x1="198" y1="42" x2="42" y2="198" stroke={iceBlue} strokeWidth="0.3" />
      </svg>
    </div>
  )

  // Shared content block
  const contentBlock = (
    <div
      ref={contentRef}
      {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')}
      style={{ position: 'absolute', inset: 0, paddingLeft: 'clamp(20px, 5vw, 60px)', paddingRight: 'clamp(20px, 5vw, 60px)', paddingBottom: 'clamp(40px, 8vw, 100px)', display: 'flex', alignItems: 'flex-end', zIndex: 2 }}
    >
      <div style={{ maxWidth: '680px' }}>
        {/* Badge — staggered reveal 0.3s */}
        {slide.badge && (
          <div
            {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')}
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              borderRadius: 4,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              background: 'rgba(184, 212, 227, 0.12)',
              color: iceBlue,
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.08em',
              textTransform: 'uppercase' as const,
              border: '1px solid rgba(184, 212, 227, 0.3)',
              marginBottom: '20px',
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateY(0)' : 'translateY(-12px)',
              transition: 'opacity 0.5s cubic-bezier(0.22,1,0.36,1) 0.3s, transform 0.5s cubic-bezier(0.22,1,0.36,1) 0.3s',
            }}
          >
            {slide.badge}
          </div>
        )}
        {/* Title — staggered reveal 0.5s */}
        <h2
          {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
          style={{
            fontFamily: "var(--font-sans), sans-serif",
            fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
            fontWeight: 400,
            lineHeight: '110%',
            color: iceBlue,
            marginBottom: 20,
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.5s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.5s',
          }}
        >
          {slide.title}
        </h2>
        {/* Subtitle — staggered reveal 0.7s */}
        <p
          {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
          style={{
            fontSize: 16,
            lineHeight: '150%',
            color: 'rgba(255,255,255,0.72)',
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.7s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.7s',
          }}
        >
          {slide.subtitle}
        </p>
        {slide.ctaLabel && (
          <a
            {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
            href={slide.ctaHref ?? '#'}
            style={{
              display: 'inline-block',
              marginTop: '28px',
              padding: '12px 28px',
              background: iceBlue,
              color: navy,
              fontSize: '15px',
              fontWeight: 500,
              textDecoration: 'none',
              letterSpacing: '0.03em',
              transition: 'background 0.3s ease, transform 0.3s ease',
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateY(0)' : 'translateY(12px)',
            }}
          >
            {slide.ctaLabel}
          </a>
        )}
      </div>
    </div>
  )

  // Scroll progress line at the bottom of each slide
  const progressLine = (
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', zIndex: 3, backgroundColor: 'rgba(184, 212, 227, 0.08)' }}>
      <div
        ref={progressRef}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: iceBlue,
          transformOrigin: 'left',
          transform: 'scaleX(0)',
          willChange: 'transform',
        }}
      />
    </div>
  )

  // Preview mode: pure CSS background-attachment: fixed
  if (!isEditing) {
    return (
      <div
        ref={wrapRef}
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          willChange: 'transform',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: navy,
          } : { backgroundColor: navy }),
        }}
      >
        {/* Layer 1: Bottom-heavy gradient for text readability */}
        <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(15, 25, 35, 0.92) 14%, rgba(15, 25, 35, 0.35) 60%, rgba(15, 25, 35, 0.10) 100%)' }} />
        {/* Layer 2: Diagonal geometric gradient (lens / prism theme) */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(135deg, rgba(184, 212, 227, 0.06) 0%, transparent 35%, rgba(184, 212, 227, 0.03) 65%, transparent 100%)' }} />
        {/* Layer 3: Subtle vignette */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'radial-gradient(ellipse at center, transparent 50%, rgba(15, 25, 35, 0.4) 100%)' }} />
        {/* Slide number top-right */}
        <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: iceBlue, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
          {String(idx + 1).padStart(2, '0')}
        </div>
        {/* Decorative lens circle */}
        {lensDecoration}
        {contentBlock}
        {progressLine}
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{ minHeight: '100svh', position: 'relative', overflow: 'hidden', backgroundColor: navy, color: '#FFFFFF', willChange: 'transform' }}
    >
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden', transform: 'translate3d(0,0,0)' }}
        />
      )}
      {/* Layer 1: Bottom-heavy gradient */}
      <div {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')} style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(360deg, rgba(15, 25, 35, 0.92) 14%, rgba(15, 25, 35, 0.35) 60%, rgba(15, 25, 35, 0.10) 100%)' }} />
      {/* Layer 2: Diagonal geometric gradient */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'linear-gradient(135deg, rgba(184, 212, 227, 0.06) 0%, transparent 35%, rgba(184, 212, 227, 0.03) 65%, transparent 100%)' }} />
      {/* Layer 3: Subtle vignette */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1, background: 'radial-gradient(ellipse at center, transparent 50%, rgba(15, 25, 35, 0.4) 100%)' }} />
      {/* Slide number */}
      <div style={{ position: 'absolute', right: 'clamp(20px, 5vw, 60px)', top: 'clamp(50px, 8vw, 100px)', zIndex: 2, fontFamily: "var(--font-sans), sans-serif", fontSize: '12px', fontWeight: 500, color: iceBlue, letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        {String(idx + 1).padStart(2, '0')}
      </div>
      {/* Decorative lens circle */}
      {lensDecoration}
      {contentBlock}
      {progressLine}
    </div>
  )
}

/* Petale parallax slide — Same technique as Miel but florist / botanical themed.
   - Preview mode (!isEditing): native CSS background-attachment:fixed.
   - Editor mode (isEditing): JS scroll-based fallback. */
function PetaleFixedBgSlide({ slide, idx, sectionId, isEditing, totalSlides }: { slide: SlideItem; idx: number; sectionId: string; isEditing?: boolean; totalSlides?: number }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)

  // JS fallback for editor mode only
  useEffect(() => {
    if (!isEditing) return
    const wrap = wrapRef.current
    const img = imgRef.current
    if (!wrap || !img) return

    let scroller: HTMLElement | null = wrap.parentElement
    while (scroller) {
      const ov = getComputedStyle(scroller).overflowY
      if (ov === 'auto' || ov === 'scroll') break
      scroller = scroller.parentElement
    }
    if (!scroller) return

    let accOffset = 0
    let el: HTMLElement | null = wrap
    while (el && el !== scroller) {
      accOffset += el.offsetTop
      el = el.offsetParent as HTMLElement | null
    }

    const setSize = () => { img.style.height = `${scroller!.clientHeight}px` }
    setSize()

    const onScroll = () => {
      img.style.transform = `translate3d(0,${-(accOffset - scroller!.scrollTop)}px,0)`
    }

    scroller.addEventListener('scroll', onScroll, { passive: true })
    onScroll()

    const ro = new ResizeObserver(setSize)
    ro.observe(scroller)

    return () => {
      scroller!.removeEventListener('scroll', onScroll)
      ro.disconnect()
    }
  }, [isEditing])

  const richBlack = '#1A1A1A'
  const roseGold = '#021734'
  const forestGreen = '#2D5016'
  const total = totalSlides ?? 3
  const progressFraction = (idx + 1) / total

  // Floating leaf/petal SVG decorative elements
  const FloatingPetals = () => (
    <>
      {/* Floating leaf — top right, gentle drift */}
      <svg
        width="60" height="80" viewBox="0 0 60 80" fill="none"
        style={{
          position: 'absolute',
          top: '15%',
          right: '8%',
          zIndex: 2,
          opacity: 0.12,
          pointerEvents: 'none',
          animation: 'petale-leaf-drift-1 12s ease-in-out infinite',
        }}
      >
        <path d="M30 0 C30 0, 60 20, 55 50 C50 80, 30 80, 30 80 C30 80, 10 80, 5 50 C0 20, 30 0, 30 0 Z" fill={roseGold} />
        <path d="M30 10 L30 70" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
        <path d="M18 30 C18 30, 24 28, 30 32" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" fill="none" />
        <path d="M42 30 C42 30, 36 28, 30 32" stroke="rgba(255,255,255,0.1)" strokeWidth="0.5" fill="none" />
      </svg>
      {/* Small petal — left side */}
      <svg
        width="35" height="45" viewBox="0 0 35 45" fill="none"
        style={{
          position: 'absolute',
          top: '35%',
          left: '5%',
          zIndex: 2,
          opacity: 0.08,
          pointerEvents: 'none',
          animation: 'petale-leaf-drift-2 15s ease-in-out infinite',
          transform: 'rotate(25deg)',
        }}
      >
        <path d="M17 0 C17 0, 35 12, 32 28 C29 45, 17 45, 17 45 C17 45, 5 45, 2 28 C-1 12, 17 0, 17 0 Z" fill={forestGreen} />
      </svg>
      {/* Tiny accent petal — bottom right */}
      <svg
        width="25" height="35" viewBox="0 0 25 35" fill="none"
        style={{
          position: 'absolute',
          bottom: '25%',
          right: '15%',
          zIndex: 2,
          opacity: 0.06,
          pointerEvents: 'none',
          animation: 'petale-leaf-drift-3 18s ease-in-out infinite',
          transform: 'rotate(-15deg)',
        }}
      >
        <path d="M12 0 C12 0, 25 10, 23 22 C21 35, 12 35, 12 35 C12 35, 3 35, 1 22 C-1 10, 12 0, 12 0 Z" fill={roseGold} />
      </svg>
    </>
  )

  // Season progress indicator — thin organic arc on right side
  const SeasonProgress = () => (
    <div style={{
      position: 'absolute',
      right: 'clamp(20px, 5vw, 60px)',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    }}>
      {/* Thin vertical progress line */}
      <div style={{ width: '1px', height: '80px', background: 'rgba(212, 165, 116, 0.15)', position: 'relative', borderRadius: '1px' }}>
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '1px',
          height: `${progressFraction * 100}%`,
          background: roseGold,
          borderRadius: '1px',
          transition: 'height 0.5s ease',
        }} />
      </div>
      {/* Season badge */}
      {slide.badge && (
        <span style={{
          fontFamily: "var(--font-sans), sans-serif",
          fontSize: '10px',
          fontWeight: 500,
          color: roseGold,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          opacity: 0.7,
        }}>
          {slide.badge}
        </span>
      )}
    </div>
  )

  // Shared overlays for both modes
  const renderOverlays = () => (
    <>
      {/* Layer 1: Bottom warm gradient for text readability */}
      <div
        {...elementProps(sectionId, `slides.${idx}.overlay`, 'container', 'Gradient Overlay')}
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
          background: 'linear-gradient(0deg, rgba(26, 26, 26, 0.95) 0%, rgba(26, 26, 26, 0.7) 25%, rgba(26, 26, 26, 0.2) 50%, rgba(26, 26, 26, 0.05) 100%)',
        }}
      />
      {/* Layer 2: Organic curved gradient (botanical feel) */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        background: 'radial-gradient(ellipse 120% 60% at 20% 100%, rgba(45, 80, 22, 0.12) 0%, transparent 60%), linear-gradient(135deg, rgba(45, 80, 22, 0.06) 0%, transparent 40%)',
      }} />
      {/* Layer 3: Soft warm vignette */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1,
        background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 40%, rgba(26, 26, 26, 0.4) 100%)',
      }} />
    </>
  )

  // Shared content block for both modes
  const renderContent = () => (
    <>
      <FloatingPetals />
      {renderOverlays()}
      <SeasonProgress />

      {/* Slide number top-right */}
      <div style={{
        position: 'absolute',
        right: 'clamp(20px, 5vw, 60px)',
        top: 'clamp(50px, 8vw, 100px)',
        zIndex: 2,
        fontFamily: "var(--font-sans), sans-serif",
        fontSize: '12px',
        fontWeight: 500,
        color: roseGold,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
      }}>
        {String(idx + 1).padStart(2, '0')}
      </div>

      {slide.badge && (
        <div
          {...elementProps(sectionId, `slides.${idx}.featuredBadge`, 'badge', 'Badge')}
          style={{
            position: 'absolute',
            left: 'clamp(20px, 5vw, 60px)',
            top: 'clamp(50px, 8vw, 100px)',
            padding: '8px 20px',
            borderRadius: 4,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            background: 'rgba(212, 165, 116, 0.12)',
            color: roseGold,
            fontSize: 14,
            fontWeight: 500,
            zIndex: 2,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            border: '1px solid rgba(212, 165, 116, 0.3)',
            opacity: 0,
            transform: 'translateX(-12px)',
            animation: 'petale-badge-drift 0.6s ease 0.3s forwards',
          }}
        >
          {slide.badge}
        </div>
      )}

      <div
        {...elementProps(sectionId, `slides.${idx}.content`, 'container', 'Slide Content')}
        style={{
          position: 'absolute', inset: 0,
          paddingLeft: 'clamp(20px, 5vw, 60px)',
          paddingRight: 'clamp(20px, 5vw, 60px)',
          paddingBottom: 'clamp(40px, 8vw, 100px)',
          display: 'flex', alignItems: 'flex-end', zIndex: 2,
        }}
      >
        <div style={{ maxWidth: '680px' }}>
          <h2
            {...elementProps(sectionId, `slides.${idx}.title`, 'heading')}
            style={{
              fontFamily: "var(--font-sans), sans-serif",
              fontSize: 'clamp(2.25rem, 1.3929rem + 3.8095vw, 4.25rem)',
              fontWeight: 400,
              lineHeight: '110%',
              color: roseGold,
              marginBottom: 20,
              opacity: 0,
              transform: 'translateY(20px) scale(0.97)',
              animation: 'petale-title-bloom 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s forwards',
            }}
          >
            {slide.title}
          </h2>
          <p
            {...elementProps(sectionId, `slides.${idx}.subtitle`, 'text')}
            style={{
              fontSize: 16,
              lineHeight: '150%',
              color: 'rgba(255,255,255,0.72)',
              opacity: 0,
              animation: 'petale-subtitle-fade 0.6s ease 0.7s forwards',
            }}
          >
            {slide.subtitle}
          </p>
          {slide.ctaLabel && (
            <a
              {...elementProps(sectionId, `slides.${idx}.cta`, 'button')}
              href={slide.ctaHref ?? '#'}
              style={{
                display: 'inline-block',
                marginTop: '28px',
                padding: '12px 28px',
                background: forestGreen,
                color: '#FFFFFF',
                fontSize: '15px',
                fontWeight: 500,
                textDecoration: 'none',
                letterSpacing: '0.03em',
                transition: 'background 0.3s ease, transform 0.3s ease',
                opacity: 0,
                animation: 'petale-subtitle-fade 0.5s ease 0.9s forwards',
              }}
            >
              {slide.ctaLabel}
            </a>
          )}
        </div>
      </div>
    </>
  )

  // Keyframes injected inline (only once per slide — lightweight)
  const keyframesStyle = (
    <style>{`
      @keyframes petale-leaf-drift-1 {
        0%, 100% { transform: translateY(0) rotate(0deg); }
        50% { transform: translateY(12px) rotate(3deg); }
      }
      @keyframes petale-leaf-drift-2 {
        0%, 100% { transform: translateY(0) rotate(25deg); }
        50% { transform: translateY(-10px) rotate(30deg); }
      }
      @keyframes petale-leaf-drift-3 {
        0%, 100% { transform: translateY(0) rotate(-15deg); }
        50% { transform: translateY(8px) rotate(-10deg); }
      }
      @keyframes petale-badge-drift {
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes petale-title-bloom {
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes petale-subtitle-fade {
        to { opacity: 1; }
      }
    `}</style>
  )

  // Preview mode: pure CSS background-attachment: fixed with will-change + transform3d
  if (!isEditing) {
    return (
      <div
        {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
        style={{
          display: 'block',
          minHeight: '100svh',
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          willChange: 'transform',
          transform: 'translate3d(0,0,0)',
          ...(imageFocal(slide.image).src ? {
            backgroundImage: `url(${imageFocal(slide.image).src})`,
            backgroundAttachment: 'fixed',
            backgroundSize: 'cover',
            backgroundPosition: imageFocal(slide.image).pos,
            backgroundColor: richBlack,
          } : { backgroundColor: richBlack }),
        }}
      >
        {keyframesStyle}
        {renderContent()}
      </div>
    )
  }

  // Editor mode: JS-based scroll compensation with <img> tag
  return (
    <div
      ref={wrapRef}
      {...elementProps(sectionId, `slides.${idx}`, 'container', 'Slide')}
      style={{
        minHeight: '100svh',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: richBlack,
        color: '#FFFFFF',
        willChange: 'transform',
        transform: 'translate3d(0,0,0)',
      }}
    >
      {keyframesStyle}
      {imageFocal(slide.image).src && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={imgRef}
          {...elementProps(sectionId, `slides.${idx}.image`, 'image', 'Slide Image')}
          src={imageFocal(slide.image).src}
          alt=""
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', objectFit: 'cover', objectPosition: imageFocal(slide.image).pos, pointerEvents: 'none', willChange: 'transform', backfaceVisibility: 'hidden' }}
        />
      )}
      {renderContent()}
    </div>
  )
}

export function SliderSection({ config, isEditing }: { config: SectionConfig; isEditing?: boolean }) {
  const content = config.content as SliderContent
  const { universe, layout } = parseVariant(config.variant || 'startup-hero')
  const uConfig = UNIVERSE_CONFIGS[universe]
  const slides = content.slides ?? []

  const { emblaRef, selectedIndex, scrollSnaps, canScrollPrev, canScrollNext, scrollPrev, scrollNext, scrollTo } = useSectionCarousel({
    loop: content.loop ?? true,
    autoplay: content.autoplay ?? true,
    interval: content.interval ?? 5000,
    align: layout === 'cards' ? 'start' : 'center',
  })

  const emitChange = useCallback((type: 'slider-change' | 'slider-next' | 'slider-prev', index: number) => {
    componentTriggerBus.emit({ type, sourceId: config.id, data: { index } })
  }, [config.id])

  useEffect(() => {
    emitChange('slider-change', selectedIndex)
  }, [selectedIndex, emitChange])

  if (slides.length === 0) return null

  // ── mosaic: galerie éditoriale en grille asymétrique (sombre) ──
  if (layout === 'mosaic') return <MosaicGallery config={config} slides={slides} />
  if (layout === 'portes') return <PortesGallery config={config} slides={slides} />

  // ── nacre-parallax: Fullscreen parallax nail art gallery ──
  if (config.variant === 'nacre-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Collection Automne', badge: 'Nouveau', subtitle: 'Tons chauds et textures velours pour un look sophistiqu\u00E9', image: '' } as SlideItem,
      { id: '2', title: 'French R\u00E9invent\u00E9e', badge: 'Tendance', subtitle: 'La classique french manucure revisit\u00E9e avec des touches dor\u00E9es', image: '' } as SlideItem,
      { id: '3', title: 'Nail Art G\u00E9om\u00E9trique', badge: 'Exclusif', subtitle: 'Lignes \u00E9pur\u00E9es et formes abstraites pour un style audacieux', image: '' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <NacreFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── obscura-parallax: Fullscreen parallax photographer portfolio ──
  if (config.variant === 'obscura-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Lumière Dorée', badge: 'Portrait', subtitle: 'Un jeu d\'ombres et de lumière naturelle pour capturer l\'essence du sujet', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Instant Éternel', badge: 'Mariage', subtitle: 'L\'émotion brute d\'un jour unique, sublimée par un regard artistique', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Horizon Urbain', badge: 'Paysage', subtitle: 'La vibration d\'un moment capturée dans sa plus pure intensité', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <ObscuraFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── canopy-parallax: Fullscreen parallax eco product slides ──
  if (config.variant === 'canopy-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Collection Été', badge: 'Coup de cœur', subtitle: 'Des sneakers légères en fibre d\'eucalyptus pour un été responsable', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Wool Runner', badge: 'Nouveau', subtitle: 'Laine mérinos premium pour un confort naturel au quotidien', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Trail Runner SWT', badge: 'Best-seller', subtitle: 'Semelle bio-based et traction tout-terrain, conçue pour durer', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <CanopyFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── brixsa-parallax: Fullscreen parallax property cards ──
  if (config.variant === 'brixsa-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Family Friendly Home', badge: '$3,900', subtitle: 'Nestled in a peaceful suburban neighbourhood, with a spacious layout spanning 1,929 square feet, this home offers the perfect blend of comfort and style.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Elegant Condo Living', badge: '$3,900', subtitle: 'Nestled in a peaceful suburban neighbourhood, with a spacious layout spanning 1,929 square feet, this home offers the perfect blend of comfort and style.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Palm Grove Mansion', badge: '$3,900', subtitle: 'Nestled in a peaceful suburban neighbourhood, with a spacious layout spanning 1,929 square feet, this home offers the perfect blend of comfort and style.', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return <BrixsaParallaxWithLightbox items={items} sectionId={config.id} isEditing={isEditing} />
  }

  // ── braise-parallax: Fullscreen parallax restaurant gallery ──
  if (config.variant === 'braise-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Cuisine Ouverte', badge: 'En vedette', subtitle: 'Notre chef orchestre une symphonie de saveurs dans une cuisine ouverte sur la salle', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Cave \u00E0 Vins', badge: 'S\u00E9lection', subtitle: 'Plus de 300 r\u00E9f\u00E9rences soigneusement s\u00E9lectionn\u00E9es par notre sommelier', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Terrasse Secr\u00E8te', badge: 'Exclusif', subtitle: 'Un \u00E9crin de verdure cach\u00E9 au c\u0153ur de la ville pour des soir\u00E9es inoubliables', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <BraiseFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── forge-parallax: Fullscreen parallax sports coach gallery ──
  if (config.variant === 'forge-parallax') {
    const defaultSlides = [
      { id: '1', title: 'La Salle', badge: 'Équipement Pro', subtitle: 'Un espace de 500m² équipé des meilleures machines pour repousser vos limites', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Outdoor Training', badge: 'En plein air', subtitle: 'Des séances en extérieur pour varier les plaisirs et profiter du grand air', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Transformations', badge: 'Résultats', subtitle: 'Des centaines de transformations réussies grâce à un accompagnement sur-mesure', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <ForgeFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── ciseaux-parallax: Fullscreen parallax hair salon gallery ──
  if (config.variant === 'ciseaux-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Notre Salon', badge: 'Bienvenue', subtitle: 'Un espace lumineux et raffiné dédié à votre beauté et votre bien-être', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Les Produits', badge: 'Premium', subtitle: 'Des soins et colorations haut de gamme pour sublimer chaque chevelure', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'L\'Expérience', badge: 'Exclusif', subtitle: 'Un moment de détente unique, pensé dans les moindres détails pour vous', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <CiseauxFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── atelier-parallax: Fullscreen parallax interior architecture gallery ──
  if (config.variant === 'atelier-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Avant / Apr\u00e8s', badge: 'Transformation', subtitle: 'Transformation compl\u00e8te d\'un appartement parisien de 120m\u00b2', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Mat\u00e9riaux nobles', badge: 'Savoir-faire', subtitle: 'S\u00e9lection rigoureuse de mat\u00e9riaux d\'exception pour chaque projet', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Lumi\u00e8re naturelle', badge: 'Design', subtitle: 'Optimisation des espaces pour maximiser la luminosit\u00e9', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <AtelierFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── encre-parallax: Fullscreen parallax tattoo studio process gallery ──
  if (config.variant === 'encre-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Consultation', badge: 'Étape 01', subtitle: 'Échange approfondi sur votre projet, recherche de références et création du design personnalisé', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Création', badge: 'Étape 02', subtitle: 'Dessin détaillé, ajustements et validation du stencil avant la séance', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Réalisation', badge: 'Étape 03', subtitle: 'Exécution précise dans un environnement stérile avec les meilleurs équipements', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <EncreFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── serenite-parallax: Fullscreen parallax spa & wellness gallery ──
  if (config.variant === 'serenite-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Espace Hammam', badge: 'Bien-être', subtitle: 'Un voyage sensoriel au cœur de la tradition orientale, entre vapeurs et gommage au savon noir', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Cabines de Soins', badge: 'Exclusivité', subtitle: 'Des espaces privatifs baignés de lumière tamisée, pensés pour une relaxation totale', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Jardin Zen', badge: 'Harmonie', subtitle: 'Un écrin de verdure où méditation et relaxation se mêlent dans une harmonie parfaite', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <SereniteFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── pulse-parallax: Fullscreen parallax DJ / music event gallery ──
  if (config.variant === 'pulse-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Festival Stage', badge: 'Festival', subtitle: "L'adrénaline de jouer devant des milliers de personnes, les basses qui font trembler le sol", image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Studio Session', badge: 'Studio', subtitle: 'Des heures de création, de sampling et de mixage pour produire le son parfait', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Club Night', badge: 'Club', subtitle: "L'intimité d'un club, la connexion directe avec le dancefloor, la magie du moment", image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <PulseFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── ascent-parallax: Fullscreen parallax business / life coach gallery ──
  if (config.variant === 'ascent-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Clarifier', badge: 'Étape 01', subtitle: 'Identifier vos objectifs profonds, lever les freins invisibles et définir une feuille de route claire vers votre réussite', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Transformer', badge: 'Étape 02', subtitle: 'Développer de nouveaux réflexes de leadership, reprogrammer vos schémas limitants et libérer votre plein potentiel', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Performer', badge: 'Étape 03', subtitle: 'Mettre en place des systèmes durables, mesurer vos progrès et ancrer les résultats dans le temps', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <AscentFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── saveur-parallax: Fullscreen parallax catering / private chef gallery ──
  if (config.variant === 'saveur-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Produits d\'exception', badge: 'Sourcing', subtitle: 'Sélection rigoureuse auprès des meilleurs producteurs locaux et maraîchers bio', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'L\'art du dressage', badge: 'Gastronomie', subtitle: 'Chaque assiette est une œuvre d\'art, pensée pour émerveiller les yeux avant les papilles', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Service sur mesure', badge: 'Excellence', subtitle: 'Une équipe dédiée, du chef au maître d\'hôtel, pour un service irréprochable', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <SaveurFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── zenith-parallax: Fullscreen parallax yoga / pilates studio gallery ──
  if (config.variant === 'zenith-parallax') {
    const defaultSlides = [
      { id: '1', title: 'La Grande Salle', badge: 'Practice', subtitle: '150m² de parquet naturel, baignés de lumière zénithale, pour pratiquer en toute sérénité', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'L\'Espace Méditation', badge: 'Méditation', subtitle: 'Un cocon de calme avec éclairage tamisé, bougies et diffusion d\'huiles essentielles', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Le Rooftop', badge: 'Plein Air', subtitle: 'Cours en plein air avec vue panoramique, pour reconnecter pratique et nature', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <ZenithFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── prisme-parallax: Fullscreen parallax optician / eyewear showcase ──
  if (config.variant === 'prisme-parallax') {
    const defaultSlides = [
      { id: '1', title: 'L\'Atelier Optique', badge: 'Expertise', subtitle: 'Un espace d\u00E9di\u00E9 o\u00F9 la pr\u00E9cision rencontre l\u2019\u00E9l\u00E9gance. Nos opticiens experts r\u00E9alisent chaque montage avec un soin m\u00E9ticuleux', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Showroom Collections', badge: 'S\u00E9lection', subtitle: 'Plus de 2000 montures des plus grandes maisons. Un choix curat\u00E9 entre cr\u00E9ateurs ind\u00E9pendants et marques iconiques', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Centre de Vision', badge: 'Technologie', subtitle: 'Les derni\u00E8res technologies d\u2019examen visuel pour un diagnostic pr\u00E9cis et une correction optimale de votre vue', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <PrismeFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── miel-parallax: Fullscreen parallax patisserie / bakery gallery ──
  if (config.variant === 'miel-parallax') {
    const defaultSlides = [
      { id: '1', title: 'Le Fournil', badge: 'Artisanat', subtitle: 'Chaque nuit, nos boulangers donnent vie à la pâte. Four à pierre, levain centenaire et passion intacte depuis 25 ans', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Le Laboratoire', badge: 'Précision', subtitle: 'Un atelier où la précision rencontre la créativité. Chaque pâtisserie est assemblée à la main avec une attention méticuleuse', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'La Boutique', badge: 'Accueil', subtitle: 'Un écrin gourmand où nos créations vous accueillent chaque matin dans une atmosphère chaleureuse et parfumée', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <MielFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── jlstudio-process: Fullscreen parallax process/timeline for JL Studio agency ──
  if (config.variant === 'jlstudio-process') {
    const defaultSlides = [
      { id: '1', title: 'Discovery', badge: 'Étape 01', subtitle: 'On échange sur votre vision, vos objectifs et votre audience cible. J\'analyse votre marché et vos concurrents pour définir la stratégie idéale.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Design', badge: 'Étape 02', subtitle: 'Je crée les maquettes de votre site avec un design unique et sur-mesure. Chaque pixel est pensé pour refléter votre identité et convertir vos visiteurs.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'Développement', badge: 'Étape 03', subtitle: 'Je code votre site avec les dernières technologies. Performance, sécurité et scalabilité sont mes priorités absolues.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '4', title: 'Tests & QA', badge: 'Étape 04', subtitle: 'Tests approfondis sur tous les navigateurs et appareils. Chaque fonctionnalité est vérifiée, chaque interaction est peaufinée.', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '5', title: 'Lancement', badge: 'Étape 05', subtitle: 'Déploiement sur infrastructure haute performance avec monitoring. Formation et support continu post-lancement.', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Process Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <JlstudioFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} />
        ))}
      </section>
    )
  }

  // ── petale-parallax: Fullscreen parallax florist / seasonal flowers gallery ──
  if (config.variant === 'petale-parallax') {
    const defaultSlides = [
      { id: '1', title: 'L\u2019Atelier Floral', badge: 'Printemps', subtitle: 'Chaque matin, nos fleuristes composent des bouquets uniques avec des fleurs fra\u00EEches s\u00E9lectionn\u00E9es aupr\u00E8s des meilleurs producteurs locaux', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '2', title: 'Les Saisons', badge: '\u00C9t\u00E9', subtitle: 'Nous suivons le rythme de la nature. Chaque saison apporte ses tr\u00E9sors floraux que nous mettons en sc\u00E8ne avec passion', image: '/demo/placeholder.svg' } as SlideItem,
      { id: '3', title: 'L\u2019\u00C9motion V\u00E9g\u00E9tale', badge: 'Automne', subtitle: 'Un bouquet est bien plus que des fleurs. C\u2019est un message, une \u00E9motion, un souvenir que nous fa\u00E7onnons pour vous', image: '/demo/placeholder.svg' } as SlideItem,
    ] as SlideItem[]
    const items = slides.length > 0 ? slides : defaultSlides

    return (
      <section {...elementProps(config.id, 'wrapper', 'container', 'Parallax Section')} style={{ width: '100%' }}>
        {items.map((slide, i) => (
          <PetaleFixedBgSlide key={slide.id} slide={slide} idx={i} sectionId={config.id} isEditing={isEditing} totalSlides={items.length} />
        ))}
      </section>
    )
  }

  // Thumbnails layout
  const isThumbnails = layout === 'thumbnails'

  return (
    <section className={cn(uConfig.bg, 'py-16 md:py-24')}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        {content.title && (
          <div className="text-center mb-8">
            {content.eyebrow && (
              <span {...elementProps(config.id, 'eyebrow', 'text')} className={cn('text-xs font-semibold uppercase tracking-widest', uConfig.eyebrow)}>
                {content.eyebrow}
              </span>
            )}
            <h2 {...elementProps(config.id, 'title', 'heading')} className={cn('text-3xl md:text-4xl font-bold mt-3', uConfig.text)}>
              {content.title}
            </h2>
          </div>
        )}

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex">
              {slides.map((slide, i) =>
                layout === 'cards' ? (
                  <CardSlide key={slide.id} slide={slide} uConfig={uConfig} sectionId={config.id} index={i} isEditing={isEditing} />
                ) : (
                  <HeroSlide key={slide.id} slide={slide} uConfig={uConfig} sectionId={config.id} index={i} isEditing={isEditing} />
                )
              )}
            </div>
          </div>

          {/* Arrows */}
          {(content.showArrows ?? true) && slides.length > 1 && (
            <>
              <button
                onClick={() => { scrollPrev(); emitChange('slider-prev', selectedIndex) }}
                disabled={!canScrollPrev}
                className={cn('absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-30', uConfig.arrowBg)}
              >
                <ChevronLeft className={cn('w-5 h-5', uConfig.arrowText)} />
              </button>
              <button
                onClick={() => { scrollNext(); emitChange('slider-next', selectedIndex) }}
                disabled={!canScrollNext}
                className={cn('absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center transition-all disabled:opacity-30', uConfig.arrowBg)}
              >
                <ChevronRight className={cn('w-5 h-5', uConfig.arrowText)} />
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {(content.showDots ?? true) && scrollSnaps.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {scrollSnaps.map((_, i) => (
              <button
                key={i}
                onClick={() => scrollTo(i)}
                className={cn('w-2.5 h-2.5 rounded-full transition-all', i === selectedIndex ? uConfig.dotActive : uConfig.dotInactive)}
              />
            ))}
          </div>
        )}

        {/* Counter */}
        {content.showCounter && slides.length > 1 && (
          <div className={cn('text-center mt-4 text-sm font-medium', uConfig.sub)}>
            {selectedIndex + 1} / {slides.length}
          </div>
        )}

        {/* Thumbnails */}
        {isThumbnails && slides.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                onClick={() => scrollTo(i)}
                className={cn(
                  'shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition-all',
                  i === selectedIndex ? 'border-indigo-500 opacity-100' : 'border-transparent opacity-60 hover:opacity-80',
                )}
              >
                {imageFocal(slide.image).src ? (
                  <img src={imageFocal(slide.image).src} alt="" className="w-full h-full object-cover" style={{ objectPosition: imageFocal(slide.image).pos }} />
                ) : (
                  <div className="w-full h-full bg-zinc-200 flex items-center justify-center">
                    <ImageIcon className="w-4 h-4 text-zinc-400" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export const sliderMeta: SectionMeta = {
  type: 'slider',
  label: 'Slider',
  icon: '🎠',
  variants: [
    'startup-hero', 'startup-cards', 'startup-thumbnails',
    'corporate-hero', 'corporate-cards', 'corporate-thumbnails',
    'luxe-hero', 'luxe-cards', 'luxe-thumbnails',
    'creative-hero', 'creative-cards', 'creative-thumbnails',
    'ecommerce-hero', 'ecommerce-cards', 'ecommerce-thumbnails',
    'glass-hero', 'glass-cards', 'glass-thumbnails',
    'nacre-parallax',
    'obscura-parallax',
    'canopy-parallax',
    'brixsa-parallax',
    'braise-parallax',
    'forge-parallax',
    'ciseaux-parallax',
    'atelier-parallax',
    'encre-parallax',
    'serenite-parallax',
    'pulse-parallax',
    'saveur-parallax',
    'ascent-parallax',
    'zenith-parallax',
    'miel-parallax',
    'jlstudio-process',
    'prisme-parallax',
    'petale-parallax',
  ],
  defaultVariant: 'startup-hero',
  defaultContent: {},
}
